
import {
    GrpcService,
    HttpRequestDelegate,
} from "../../../grpc"

import {
    CameraData,
    Extrinsics,
} from "../../../sennotech/scanner/v2/camera"

import {
    Language,
} from "../../../sennotech/scanner/v2/type"

import {
    Timestamp,
} from "../../../google/protobuf/timestamp"

import {
    Store,
} from "../../../sennotech/scanner/v2/store"

import {
    Examinee,
} from "../../../sennotech/scanner/v2/examinee"

import {
    Empty,
} from "../../../google/protobuf/empty"

export interface CreateExaminationRequest {
    storeId: number
    serialNumber: string /* 设备序列号 */
    pressureData: PressureData /* 足压数据 */
    cameraData: Array<CameraData> /* 相机数据 */
    extrinsics: Array<Extrinsics> /* 相机外参 */
    enhanceExtrinsics: Array<Extrinsics> /* 增强外参, 可选 */
}

export interface CreateExaminationResponse {
    ticket: string
    storeId: number
    serialNumber: string /* 设备序列号 */
    error: ExaminationError /* 检测错误类型 */
}

export interface ExaminationError {
    code: number
    message: string
}

export interface BindExaminationExamineeRequest {
    ticket: string
    examineeId: number /* 被检测者ID */
}

export interface ReportProcessedNotificationRequest {
    ticket: string
}

export interface GetReportByTicketRequest {
    ticket: string
    language: Language
}

export interface Report {
    header: Header
    body: Body
}

/*------------------FootPressure------------------------- */
export interface FootPressure {
    thumbnail: Thumbnail
    left: Data
    right: Data
    cop: Cop
    featureFrameDataTifUrl: string /* 特征图,可用于山峰图 */
}

export interface VarusTypeData {
    rearInnerValue: number /* 内翻值 */
    rearOuterValue: number /* 外翻值 */
    varusType: VarusType /* 内外翻类型 */
    level: HeelLoadLevel /* 足跟负荷水平 */
}

export interface Thumbnail {
    url: string
    copX: number
    copY: number
    centroidX: number
    centroidY: number
    forceMaxPointX: number
    forceMaxPointY: number
    width: number
    height: number
}

/*------------------FootShape----------------------------- */
export interface FootShape {
    valid: boolean
    left: Data
    right: Data
}

export interface DeleteExaminationRequest {
    ticket: string
}

export interface ListReportsRequest {
    pageNum: number
    pageSize: number
    institutionId?: number /* 机构ID */
    storeId?: number /* 店铺ID */
    storeName?: string /* 店铺名称 */
    examineeId?: number /* 被检测者id */
    nickname?: string /* 被检测者昵称 */
    mobileNumber?: string /* 被检测者手机号 */
    serialNumber?: string /* 设备序列号 */
    language: Language /* 报告语言 */
    startTime?: Timestamp /* 开始时间 */
    endTime?: Timestamp /* 结束时间 */
}

export interface ListReportsResponse {
    reports: Array<Header>
    totalElements: number
    totalPages: number
    isFirst: boolean
    isLast: boolean
    pageElements: number
    pageNum: number
    pageSize: number
}

export interface Examiner {
    id: number
    nickname: string /* 昵称 */
    mobileNumber?: string /* 手机号 */
    email?: string /* 邮箱 */
}

export interface BatchGetReportInfoRequest {
    examineeIds: Array<number>
}

export interface BatchGetReportInfoResponse {
    reportInfo: Array<ReportInfo>
}

export interface ReportInfo {
    examineeId: number
    latestTime: Timestamp /* 最新报告时间 */
    count: number /* 报告次数 */
}

export interface UpdateExaminationRemarkRequest {
    ticket: string /* 报告ticket */
    remark: string /* 备注 */
}

export interface ShareReportByEmailRequest {
    email: string /* 收件地址 */
    ticket: string /* 报告ticket */
    language: Language
}

export interface ExportFootShapeModelRequest {
    ticket: string
}

export interface ExportFootShapeModelResponse {
    footShapeModelZip: string
}

export interface ExportExaminationReportRequest {
    storeId: number
    examineeId?: number /* ID */
    startTimestamp?: number /* 开始日期 */
    endTimestamp?: number /* 结束日期 */
    language: Language
}

export interface ExportExaminationReportResponse {
    examinationDataExcel: string
}

export interface CreateExportTaskRequest {
    organizationId: number
    startTime: Timestamp /* 开始日期 */
    endTime: Timestamp /* 结束日期 */
    language: Language
}

export interface CreateExportTaskResponse {
    success: boolean
}

export interface GetExportTaskRequest {
    organizationId: number
}

export interface ExportTask {
    id: number
    createdAt: Timestamp /* 创建时间 */
    clearedAt: Timestamp /* 清除时间 */
    startTime: Timestamp /* 开始日期 */
    endTime: Timestamp /* 结束日期 */
    status: string /* pending, processing, completed, failed */
    progress: number /* 0-100 */
    url: string
}

export interface GetExportTaskResponse {
    exportTasks: Array<ExportTask>
}

export interface DeleteExportTaskRequest {
    exportTaskId: number
}

export interface RetryExportTaskRequest {
    exportTaskId: number
}

export interface GetCalibrateDataRequest {
    uuid: string
}

export interface GetCalibrateDataResponse {
    extrinsics: string
    cameraDatas: Array<Data>
}

export interface GetCameraDataRequest {
    ticket: string
}

export interface GetCameraDataResponse {
    cameraData: Array<Data>
    extrinsics: Array<string>
}

export interface GetReportQrCodeRequest {
    ticket: string
}

export interface GetReportQrCodeResponse {
    url: string
}

export interface PressureData {
    objectKey: string /* 存储对象key值 */
    compression?: string /* 压缩格式，不传值表示没有压缩 */
}

export interface Header {
    ticket: string /* 报告ticket */
    serialNumber: string /* 设备序列号 */
    state: ProcessingState /* 处理状态 */
    error: ExaminationError /* 报告错误类型 */
    createTime: Timestamp /* 创建时间 */
    remark?: string /* 备注 */
    readonlyUrl: string /* 报告页面 */
    editableUrl: string /* 报告页面 */
    store: Store /* 店铺信息 */
    examiner: Examiner /* 检测者信息 */
    examinee: Examinee /* 被检测者信息 */
}

export interface Body {
    footShape: FootShape /* 脚型数据 */
    footPressure: FootPressure /* 静态足压数据 */
}

export interface Cop {
    x: Array<number>
    y: Array<number>
    centroidX: number
    centroidY: number
    distance: number /* c.o.p.距离 */
    speed: number /* 平均速度 */
    lsf: number /* 距离/表面积 （LSF） */
    ellipse: CopEllipse
    physicalWidth: number
    physicalHeight: number
}

export interface CopEllipse {
    centerX: number
    centerY: number
    radiusX: number
    radiusY: number
    rotate: number /* 旋转弧度值 */
    eccentricity: number /* 偏心率 */
    area: number /* 重心偏移 */
}

export interface Data {
    anterior: Part /* 前足 */
    posterior: Part /* 后足 */
    whole: Part /* 全脚 */
    forefoot: Part /* 前脚掌 */
    midfoot: Part /* 中足 */
    heel: Part /* 脚跟 */
    arch: FootArch /* 足弓 */
    archForefootValue: number /* 前掌足弓值 */
    archHindfootValue: number /* 后足足弓值 */
    varusTypeData: VarusTypeData /* 内外翻数据 */
    thumbnail: Thumbnail /* 彩虹图 */
    cop: Cop /* 足压中心 */
    heelPressureMapUrl: string /* 足底承压图 */
    toesForefootHorizontalLine: number /* 脚趾前脚掌水平分割线 */
    forefootMidfootHorizontalLine: number /* 前脚掌中足水平分割线 */
    midfootHeelHorizontalLine: number /* 中足脚跟水平分割线 */
    croppedThumbnailUrl: string /* 裁剪后的缩略图 */
    eversionInversionOfFootList: Array<EversionInversionOfFootType> /* 足部内外翻结论 */
    /* 足部区域的划分 */
    t1: Part /* 拇指 */
    t2345: Part /* 小脚趾 */
    m1: Part
    m2: Part
    m3: Part
    m4: Part
    m5: Part
    mrf: Part /* 脚跟内侧 */
    lrf: Part /* 脚跟外侧 */
    regionImage: RegionImage /* 足部区域图示 */
    clarke: Clarke
}

export interface Clarke {
    angle: number
    forefootOuter: Point
    hindfootOuter: Point
    m1m2Forefoot: Point
}

export interface Part {
    area: number /* 面积 */
    areaOverSelfSide: number /* 面积/全脚 */
    load: number /* 力 */
    loadOverBothSide: number /* 当前负荷比双脚负荷 */
    loadOverSelfSide: number /* 当前负荷比同侧脚负荷 */
    pressureMax: number /* 最大压强 */
    pressureMean: number /* 平均压强 */
}

export interface FootArch {
    value: number /* 足弓值 足弓AI指数 */
    level: Level /* 严重程度 */
    referenceRanges: Map<string, Range> /* 参考范围 */
}

export interface RegionImage {
    imageUrl: string /* 图 */
    t1: Point
    t2345: Point
    m1: Point
    m2: Point
    m3: Point
    m4: Point
    m5: Point
    mf: Point
    mrf: Point /* MH */
    lrf: Point /* LH */
    vLine: Line /* vertical line，足轴线（注意：17到24的线 one,another 均为0时无意义） */
    toesForefootVLine: Line /* 脚趾与前足的水平分割线 */
    forefootMidfootVLine: Line /* 前足与中足的水平分割线 */
    midfootHeelVLine: Line /* midfoot heel horizontal line 中足与足跟的水平分割线 */
    t1T2345VLine: Line
    m1M2VLine: Line
    m2M3VLine: Line
    m3M4VLine: Line
    m4M5VLine: Line
}

export interface Line {
    onePoi: Point
    anotherPoi: Point
}

export interface Point {
    x: number /* x与y同时为0没有意义 */
    y: number
}

export interface Range {
    min: number
    max: number
}

export interface Data {
    model3dUrl: string
    fileType: FileType
    footLength: number /* 脚长 */
    treadLength: number /* 着地点长 */
    footWidth: number /* 水平脚宽 */
    toeWidth: number /* 水平趾宽 */
    heelWidth: number /* 踵心全宽 */
    medialMpjLength: number /* 第一跖趾长 */
    lateralMpjLength: number /* 第五跖趾长 */
    heelLength: number /* 踵心长 */
    maximumFootWidth: number /* 最大脚宽 */
    obliqueToeWidth: number /* 斜趾宽 */
    obliqueFootWidth: number /* 斜脚宽 */
    toeDepth: number /* 脚趾厚度 */
    mpjHeight: number /* 跖趾高 */
    instepHeight: number /* 跗骨高（脚背高） */
    ballGirth: number /* 跖趾围长 */
    waistGirth: number /* 腰围 */
    instepGirth: number /* 跗围 */
    archHeight: number /* 足弓高 */
    toeWidthAngle: number /* 斜趾宽角 */
    heelGirth: number /* 兜跟围「Heel girth」 */
    innerBigToeWidth: number /* 拇指里宽「Inner width of big toe 」 */
    outerLittleToeWidth: number /* 小指外宽「Outer width of little toe」 */
    innerMpj1Width: number /* 第一跖趾里宽「1st meta-phal inner width」 */
    outerMpj5Width: number /* 第五跖趾外宽「5th meta-phal outer width」 */
    outerWaistWidth: number /* 腰窝外宽「Outer width of waist」 */
    bigToeContactLength: number /* 拇指外凸点部位长「Length of big toe contact point」 */
    littleToeContactLength: number /* 小趾外凸点部位长「Length of little toe contact point」 */
    waistPointLength: number /* 腰窝部位长度「Length of waist point」 */
    printLength: number /* 脚印长度「Print length」 */
    heelPointHeight: number /* 后跟凸点高度「Heel point height」 */
    heelRoom: number /* 后容差「Room around heel」 */
    instepPointLength: number /* 跗骨部位长度「Length of instep point」 */
    halluxAngle: number /* 拇趾翻转角度「Hallux angle」 */
    toeTip1Length: number /* 第一趾端点长度「Length of big toe tip」 */
    toeTip2Length: number /* 第二趾端点长度「Length of second toe tip」 */
    toeTip5Length: number /* 第五趾端点长度「Length of little toe tip」 */
    footWidthPercent: number /* 水平脚宽 %「Foot width %」 */
    footWidthObliquePercent: number /* 斜脚宽 %「Oblique foot width %」 */
    innerMpj1WidthPercent: number /* 第一跖趾里宽 %「1st meta-phal inner width %」 */
    outerMpj5WidthPercent: number /* 第五跖趾外宽 %「5th meta-phal outer width %」 */
    toeWidthPercent: number /* 水平趾宽 %「Toe width %」 */
    obliqueToeWidthPercent: number /* 斜趾宽 %「Oblique toe width %」 */
    innerBigToeWidthPercent: number /* 拇指里宽 %「Inner width of big toe %」 */
    outerLittleToeWidthPercent: number /* 小指外宽 %「Outer width of little toe %」 */
    mpj1LengthPercent: number /* 第一跖趾长 %「Medial MPJ length %」 */
    mpj5LengthPercent: number /* 第五跖趾长 %「Lateral MPJ length %」 */
    bigToeContactLengthPercent: number /* 拇指外凸点部位长 %「Length of big toe contact point %」 */
    littleToeContactLengthPercent: number /* 小趾外凸点部位长 %「Length of little toe contact point %」 */
    treadLengthPercent: number /* 着地点长 %「Tread length %」 */
    ballGirthPercent: number /* 跖趾围长 %「Ball girth %」 */
    footType: FootType /* 脚趾形状 */
    hallexValgusLevel: HallexValgusLevel /* 脚拇指内外翻情况 */
    angleOfVarus: number /* 内外翻角度 */
    forefootAreaRate: number /* 前脚掌面积比例「Forefoot area rate」 */
    midfootAreaRate: number /* 中脚掌面积比例「Midfoot area rate」 */
    hindfootAreaRate: number /* 后脚掌面积比例「Hindfoot area rate」 */
    footArchReferenceRanges: Map<string, Range> /* 足弓参考范围 */
}

export interface Range {
    min: number
    max: number
}

export interface Data {
    intrinsics: string
    depthUrl: string
    irUrl: string
}

export interface Data {
    intrinsics: string
    depthUrl: string
    irUrl: string
    position: string /* 相机位置 */
}

export enum VarusType {
    VARUS_TYPE_UNSPECIFIED = 0,
    VARUS = 1, /* 足内翻 */
    VALGUS = 2, /* 足外翻 */
    NORMAL = 3, /* 正常 */
    VARUS_MILD = 4, /* 轻度后足内翻 */
    VALGUS_MILD = 5, /* 轻度后足外翻 */
}

export enum FileType {
    FILE_TYPE_UNSPECIFIED = 0,
    ZIP = 1, /* zip压缩文件 */
    STL = 2, /* stl文件 */
}

export enum ProcessingState {
    PROCESSING_STATE_UNSPECIFIED = 0,
    PROCESSING = 1, /* 处理中 */
    SUCCESS = 2, /* 处理成功 */
    FAIL = 3, /* 处理失败 */
}

export enum EversionInversionOfFootType {
    UNSPECIFIED = 0,
    NORMAL = 1, /* 正常 */
    FOREFOOT_INVERSION = 3, /* 前足内翻 */
    FOREFOOT_EVERSION = 4, /* 前足外翻 */
    HEEL_INVERSION = 6, /* 足跟内翻 */
    HEEL_EVERSION = 7, /* 足跟外翻 */
    FOOT_INVERSION = 9, /* 足内翻 */
    FOOT_EVERSION = 10, /* 足外翻 */
}

export enum Level {
    UNSPECIFIED = 0,
    HIGH_SEVERE = 1, /* 重度高足弓 */
    HIGH = 2, /* 高足弓 */
    HIGH_MILD = 3, /* 轻度高足弓 */
    NORMAL = 4, /* 中足弓 */
    FLAT_MILD = 5, /* 轻度扁平足 */
    FLAT = 6, /* 扁平足 */
    FLAT_SEVERE = 7, /* 重度扁平足 */
}

export enum HeelLoadLevel {
    UNSPECIFIED = 0,
    NORMAL = 1, /* 内外侧承压均衡 */
    BIASED_OUTER = 2, /* 承压偏向外侧 */
    BIASED_INNER = 3, /* 承压偏向外侧 */
    BIASED_OUTER_EXCESSIVE = 4, /* 承压过度偏向外侧 */
    BIASED_INNER_EXCESSIVE = 5, /* 承压过度偏向外侧 */
}

export enum FootType {
    FOOT_TYPE_UNSPECIFIED = 0,
    GREEK = 1, /* 希腊脚 */
    ROMAN = 2, /* 罗马脚 */
    EGYPTIAN = 3, /* 埃及脚 */
}

export enum HallexValgusLevel {
    HALLEX_VALGUS_LEVEL_UNSPECIFIED = 0,
    VARUS_SERVER = 1, /* 重度拇内翻 */
    VARUS_MILD = 2, /* 轻度拇内翻 */
    VALGUS_LEVEL_NORMAL = 3, /* 正常脚 */
    VALGUS_MILD = 4, /* 轻度拇外翻 */
    VALGUS_SEVERE = 5, /* 重度拇外翻 */
}

export class ExaminationService extends GrpcService {
    constructor(host: string, httpRequestDelegate: HttpRequestDelegate) {
        super(host, httpRequestDelegate)
    }
    
    async createExamination(request: CreateExaminationRequest): Promise<CreateExaminationResponse> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/CreateExamination"
        return await this.call(method, path, request) as CreateExaminationResponse
    }
    
    /* 删除检测报告 */
    async deleteExamination(request: DeleteExaminationRequest): Promise<Empty> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/DeleteExamination"
        return await this.call(method, path, request) as Empty
    }
    
    /* 绑定被检测者 */
    async bindExaminationExaminee(request: BindExaminationExamineeRequest): Promise<Empty> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/BindExaminationExaminee"
        return await this.call(method, path, request) as Empty
    }
    
    /* 报告项处理完成回调 */
    async reportProcessedNotification(request: ReportProcessedNotificationRequest): Promise<Empty> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/ReportProcessedNotification"
        return await this.call(method, path, request) as Empty
    }
    
    /* 根据ticket获取报告 */
    async getReportByTicket(request: GetReportByTicketRequest): Promise<Report> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/GetReportByTicket"
        return await this.call(method, path, request) as Report
    }
    
    /* 获取二维码报告链接（客户端转二维码） */
    async getReportQrCode(request: GetReportQrCodeRequest): Promise<GetReportQrCodeResponse> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/GetReportQrCode"
        return await this.call(method, path, request) as GetReportQrCodeResponse
    }
    
    /* 列表获取检测报告 */
    async listReports(request: ListReportsRequest): Promise<ListReportsResponse> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/ListReports"
        return await this.call(method, path, request) as ListReportsResponse
    }
    
    /* 批量获取报告次数 */
    async batchGetReportInfos(request: BatchGetReportInfoRequest): Promise<BatchGetReportInfoResponse> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/BatchGetReportInfos"
        return await this.call(method, path, request) as BatchGetReportInfoResponse
    }
    
    /* 修改报告备注 */
    async updateExaminationRemark(request: UpdateExaminationRemarkRequest): Promise<Empty> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/UpdateExaminationRemark"
        return await this.call(method, path, request) as Empty
    }
    
    /* 通过邮箱分享报告 */
    async shareReportByEmail(request: ShareReportByEmailRequest): Promise<Empty> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/ShareReportByEmail"
        return await this.call(method, path, request) as Empty
    }
    
    /* 导出足型脚膜 */
    async exportFootShapeModel(request: ExportFootShapeModelRequest): Promise<ExportFootShapeModelResponse> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/ExportFootShapeModel"
        return await this.call(method, path, request) as ExportFootShapeModelResponse
    }
    
    /* 以表格的形式导出检测报告 */
    async exportExaminationReport(request: ExportExaminationReportRequest): Promise<ExportExaminationReportResponse> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/ExportExaminationReport"
        return await this.call(method, path, request) as ExportExaminationReportResponse
    }
    
    async createExportTask(request: CreateExportTaskRequest): Promise<CreateExportTaskResponse> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/CreateExportTask"
        return await this.call(method, path, request) as CreateExportTaskResponse
    }
    
    async getExportTask(request: GetExportTaskRequest): Promise<GetExportTaskResponse> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/GetExportTask"
        return await this.call(method, path, request) as GetExportTaskResponse
    }
    
    async deleteExportTask(request: DeleteExportTaskRequest): Promise<Empty> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/DeleteExportTask"
        return await this.call(method, path, request) as Empty
    }
    
    /* 重试导出 */
    async retryExportTask(request: RetryExportTaskRequest): Promise<Empty> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/RetryExportTask"
        return await this.call(method, path, request) as Empty
    }
    
    /* 获取标定数据 */
    async getCalibrateData(request: GetCalibrateDataRequest): Promise<GetCalibrateDataResponse> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/GetCalibrateData"
        return await this.call(method, path, request) as GetCalibrateDataResponse
    }
    
    /* 获取相机数据，内参、外参、depth、ir  (仅供调试使用) */
    async getCameraData(request: GetCameraDataRequest): Promise<GetCameraDataResponse> {
        const method = "post"
        const path = "/scanner.v2.ExaminationService/GetCameraData"
        return await this.call(method, path, request) as GetCameraDataResponse
    }
    
}
